<template>
  <div class="bg-white rounded divide-y divide-gray-200 border border-gray-400">
    <VFilterGroup
      v-for="(filterGroup, key) in internalFilterGroups"
      :key="key"
      v-bind="filterGroup"
      :allowed-filters="internalAllowedFilters"
      @update:value="filterGroup.value = $event"
      @update:filter-value="updateFilterValue"
    />

    <div class="p-4 space-x-2">
      <BaseButton primary :disabled="!hasChanges" @click="applyFilters"> Apply </BaseButton>
      <BaseButton ghost :disabled="!hasChanges" @click="resetFilters"> Reset </BaseButton>
      <BaseButton ghost :disabled="!hasFilters" @click="clearFilters"> Clear </BaseButton>
    </div>
  </div>
</template>

<script>
import { isEqual, cloneDeep } from 'lodash-es';
import VFilterGroup from './VFilterGroup.vue';

export default {
  name: 'VFilters',
  components: { VFilterGroup },
  props: {
    allowedFilters: {
      type: Object,
      required: true,
    },
    filterGroups: {
      type: Object,
      required: true,
    },
    defaultFilters: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      internalAllowedFilters: {},
      internalFilterGroups: {},
    };
  },
  computed: {
    appliedFilter() {
      return Object.keys(this.defaultFilters).reduce((carry, key) => {
        if (!(key in this.internalAllowedFilters)) {
          return carry;
        }

        const allowedFilter = this.internalAllowedFilters[key];
        const value = this.defaultFilters[key];

        return {
          ...carry,
          [key]:
            typeof allowedFilter.type() === typeof value
              ? value
              : this.parseValue(allowedFilter.type, value),
        };
      }, {});
    },
    currentFilter() {
      return Object.keys(this.internalFilterGroups)
        .map((name) => this.internalFilterGroups[name])
        .reduce((carry, filterGroup) => {
          if (!filterGroup.value) {
            return carry;
          }

          const selectedFilter = this.internalAllowedFilters[filterGroup.value];

          if (Array.isArray(selectedFilter.value) && selectedFilter.value.length === 0) {
            return carry;
          }

          return {
            ...carry,
            [selectedFilter.name]: selectedFilter.value,
          };
        }, {});
    },
    hasChanges() {
      return isEqual(this.appliedFilter, this.currentFilter) === false;
    },
    hasFilters() {
      return Object.keys(this.currentFilter).length > 0;
    },
  },
  watch: {
    internalFilterGroups(cur, prev) {
      if (cur.length < prev.length) {
        prev
          .filter(
            (prevFilterGroup) =>
              cur.findIndex(
                (appliedFilterGroup) => appliedFilterGroup.name === prevFilterGroup.name
              ) === -1
          )
          .forEach((passedFilterGroup) => {
            if (Array.isArray(passedFilterGroup.variations)) {
              // eslint-disable-next-line no-param-reassign
              passedFilterGroup.value = null;

              passedFilterGroup.variations.forEach((variation) => {
                if (!variation.options) {
                  return;
                }

                // eslint-disable-next-line no-param-reassign
                variation.value = variation.multiple ? [] : null;
              });
            }
          });
      }
    },
  },
  created() {
    this.internalAllowedFilters = cloneDeep(this.allowedFilters);
    this.internalFilterGroups = cloneDeep(this.filterGroups);
    this.mergeDefaultFilters();
    this.setupInternalFilterGroups();
  },
  methods: {
    updateFilterValue({ key, value }) {
      this.internalAllowedFilters[key].value = value;
    },
    mergeDefaultFilters() {
      Object.keys(this.defaultFilters).forEach((key) => {
        if (!(key in this.internalAllowedFilters)) {
          return;
        }

        const { type } = this.internalAllowedFilters[key];
        const value = this.defaultFilters[key];

        this.internalAllowedFilters[key].value =
          typeof type() === typeof value ? value : this.parseValue(type, value);
      });
    },
    parseValue(type, value) {
      if (Array.isArray(value)) {
        return value.map((v) => this.parseValue(type, v));
      }

      return type(value);
    },
    setupInternalFilterGroups() {
      Object.keys(this.defaultFilters).forEach((defaultFilter) => {
        Object.keys(this.internalFilterGroups).forEach((filterGroup) => {
          if (this.internalFilterGroups[filterGroup].filters.indexOf(defaultFilter) > -1) {
            this.internalFilterGroups[filterGroup].value = defaultFilter;
          }
        });
      });
    },
    applyFilters() {
      const { filter = {}, ...restQuery } = this.$route.query;
      const { search } = filter;

      this.$router.push({
        name: this.$route.name,
        params: {
          ...this.$route.params,
        },
        query: {
          filter: {
            ...(typeof search !== 'undefined' ? { search } : {}),
            ...this.currentFilter,
          },
          ...restQuery,
        },
      });
    },
    resetFilters() {
      this.mergeDefaultFilters();
      this.setupInternalFilterGroups();
    },
    clearFilters() {
      Object.keys(this.internalFilterGroups).forEach((filterGroup) => {
        this.internalFilterGroups[filterGroup].value = null;
      });
    },
  },
};
</script>
